import { useQuery } from "@apollo/client";
import { useTheme } from "styled-components";
import { FiAward } from "react-icons/fi";

import { Avatar } from "MVVM/Components/AvatarV2";
import { REFERRAL_LEADERBOARD } from "MVVM/GraphQL/homepage";
import { useUser } from "MVVM/Hooks/useUser";
import { trackFeedClick } from "../../analytics";
import { WidgetCard, WidgetHeader, WidgetTitle } from "../styledComponents";
import { showReferralLeaderboard } from "./audience";
import {
  IReferralLeaderboardEntry,
  IReferralStanding,
  referralsLabel,
  resetLabel,
} from "./format";
import {
  Count,
  Footer,
  InviteLink,
  List,
  NameLink,
  Rank,
  Reset,
  Row,
  YourRank,
  YouLabel,
} from "./styledComponents";

// The board shows 5; fetching 10 tells whether it's full enough to show
// everyone.
const FETCH_LIMIT = 10;
const BOARD_SIZE = 5;

const rankColor = (
  rank: number,
  theme: { colors: Record<string, string> },
): string | undefined => {
  if (rank === 1) return theme.colors.goldStar ?? "#ffd700";
  if (rank === 2) return theme.colors.chalk;
  if (rank === 3) return theme.colors.rare;
  return undefined;
};

export const ReferralLeaderboardWidget = () => {
  const theme = useTheme() as { colors: Record<string, string> };
  const { allstarUser } = useUser();
  const viewerName = allstarUser?.user?.profile?.username;
  const { data } = useQuery(REFERRAL_LEADERBOARD, {
    variables: { limit: FETCH_LIMIT },
    fetchPolicy: "cache-and-network",
  });

  const board = data?.referralLeaderboard;
  const entries: IReferralLeaderboardEntry[] = board?.entries ?? [];
  const userGroupType = allstarUser?.user?.userGroup?.type;
  if (!board || !showReferralLeaderboard(userGroupType, entries.length))
    return null;

  const viewer: IReferralStanding | null = board.viewer;
  const track = (target: string) =>
    trackFeedClick("referral_leaderboard", { target });

  return (
    <WidgetCard as="section" aria-label="Referral leaderboard">
      <WidgetHeader>
        <WidgetTitle>
          <FiAward size={15} />
          Top referrers this month
        </WidgetTitle>
        <Reset>{resetLabel(board.periodEnd)}</Reset>
      </WidgetHeader>

      <List>
        {entries.slice(0, BOARD_SIZE).map((entry) => {
          const isYou = entry.user.username === viewerName;
          const color = rankColor(entry.rank, theme);
          return (
            <Row key={entry.user.id} $highlighted={isYou}>
              <Rank $color={color}>#{entry.rank}</Rank>
              <Avatar
                size="xs"
                src={entry.user.avatarUrl}
                profileCardUsername={entry.user.username}
              />
              <NameLink
                to={`/u/${entry.user.username}`}
                $color={color}
                onClick={() => track("profile")}
              >
                {entry.user.username}
                {isYou && <YouLabel> (you)</YouLabel>}
              </NameLink>
              <Count aria-label={referralsLabel(entry.count)}>
                {entry.count.toLocaleString()}
              </Count>
            </Row>
          );
        })}
      </List>

      <Footer>
        <YourRank>
          <small>Your rank</small>
          {viewer?.rank ? `#${viewer.rank}` : "Unranked"}
        </YourRank>
        <InviteLink to="/referrals" onClick={() => track("invite")}>
          Invite friends
        </InviteLink>
      </Footer>
    </WidgetCard>
  );
};
